import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search, Mail, Phone, MapPin, FileText, Filter, Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
const Kunden = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);  const kunden = [
    {
      id: 1,
      name: 'Max Müller',
      email: 'max.mueller@email.com',
      telefon: '+49 170 1234567',
      adresse: 'Musterstraße 12, 10115 Berlin',
      gutachtenAnzahl: 3,
      letzterKontakt: '15.11.2024'
    },
    {
      id: 2,
      name: 'Anna Schmidt',
      email: 'anna.schmidt@email.com',
      telefon: '+49 171 2345678',
      adresse: 'Hauptstraße 45, 80331 München',
      gutachtenAnzahl: 2,
      letzterKontakt: '12.11.2024'
    },
    {
      id: 3,
      name: 'Peter Wagner',
      email: 'peter.wagner@email.com',
      telefon: '+49 172 3456789',
      adresse: 'Elbchaussee 78, 22763 Hamburg',
      gutachtenAnzahl: 4,
      letzterKontakt: '10.11.2024'
    }
  ];

  const filteredKunden = kunden.filter(k =>
    k.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    k.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Upload Handler
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Überprüfe Dateityp
    const validTypes = ['.xml', '.csv', '.json', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      toast.error('Ungültiges Dateiformat', {
        description: 'Bitte laden Sie eine XML, CSV, JSON oder XLSX Datei hoch.'
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/kunden/import`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setUploadResult({
          success: true,
          message: data.message || 'Kundendaten erfolgreich importiert',
          imported: data.imported || 0,
          errors: data.errors || 0
        });
        toast.success('Import erfolgreich!', {
          description: `${data.imported || 0} Kunden wurden importiert.`,
        });
      } else {
        throw new Error(data.error || 'Import fehlgeschlagen');
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.message || 'Fehler beim Importieren der Daten'
      });
      toast.error('Import fehlgeschlagen', {
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  // Drag & Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Kundendaten Import */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader className="border-b border-blue-200">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 rounded-lg p-2">
                <Upload className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-800">Kundendaten Import</CardTitle>
                <p className="text-sm text-gray-600 mt-1">Laden Sie Ihre Kundendaten als XML, CSV, JSON oder XLSX Datei hoch</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                uploading 
                  ? 'border-green-500 bg-green-50' 
                  : isDragging 
                    ? 'border-blue-500 bg-blue-50 scale-105 cursor-pointer' 
                    : 'border-blue-300 bg-white hover:border-blue-500 cursor-pointer'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".xml,.csv,.json,.xlsx"
                className="hidden"
                id="kunden-file-upload"
                onChange={handleFileInputChange}
                disabled={uploading}
              />
              
              {uploading ? (
                <div className="py-4">
                  <div className="relative mx-auto mb-4" style={{ width: '80px', height: '80px' }}>
                    <svg className="animate-spin" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                      <circle cx="50" cy="50" r="40" stroke="#3b82f6" strokeWidth="8" fill="none" strokeDasharray="251.2" strokeDashoffset="62.8" strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-blue-600 animate-pulse" />
                    </div>
                  </div>
                  <p className="text-blue-700 font-bold text-lg mb-2">Kundendaten werden importiert...</p>
                  <p className="text-sm text-blue-600">Bitte warten Sie einen Moment</p>
                </div>
              ) : (
                <label htmlFor="kunden-file-upload" className="cursor-pointer block">
                  {isDragging ? (
                    <>
                      <Upload className="w-16 h-16 text-blue-600 mx-auto mb-3 animate-bounce" />
                      <p className="text-blue-700 font-bold mb-1 text-lg">Datei hier ablegen!</p>
                      <p className="text-sm text-blue-600">Lassen Sie die Datei los</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                      <p className="text-gray-700 font-medium mb-1">Kundendaten hochladen</p>
                      <p className="text-sm text-gray-500">Klicken zum Hochladen oder Drag & Drop</p>
                      <p className="text-xs text-gray-400 mt-2">XML, CSV, JSON, XLSX - Max. 10MB</p>
                    </>
                  )}
                </label>
              )}
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`mt-4 p-4 rounded-lg flex items-start space-x-3 ${
                uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {uploadResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {uploadResult.message}
                  </p>
                  {uploadResult.imported !== undefined && (
                    <p className="text-sm text-gray-600 mt-1">
                      Importiert: {uploadResult.imported} | Fehler: {uploadResult.errors || 0}
                    </p>
                  )}
                </div>
                <button onClick={() => setUploadResult(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kunden</h1>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Kundenkontakte</p>
          </div>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kunde
          </Button>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Suche nach Name oder E-Mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 bg-gray-50 border-0 focus:bg-white"
                />
              </div>
              <Button variant="outline" className="h-12">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Kunden Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredKunden.map((kunde) => (
            <Card key={kunde.id} className="border-0 shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {kunde.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{kunde.name}</h3>
                      <div className="text-xs text-gray-500">Letzter Kontakt: {kunde.letzterKontakt}</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">Details</Button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <a href={`mailto:${kunde.email}`} className="hover:text-blue-600">{kunde.email}</a>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <Phone className="w-4 h-4 text-green-600" />
                    <a href={`tel:${kunde.telefon}`} className="hover:text-green-600">{kunde.telefon}</a>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span>{kunde.adresse}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="font-medium">{kunde.gutachtenAnzahl} Gutachten</span>
                  </div>
                  <Button variant="link" className="text-blue-600 p-0 h-auto">Alle anzeigen →</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Kunden;